import { useKeyboardShortcutsModal } from "@circle-react-shared/uikit/HeaderV3/KeyboardShortcutsModal";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { signOutPath } from "@circle-react/helpers/urlHelpers";
import { APPEARANCE, useThemeContext } from "@circle-react/providers/Theme";
import type { MenuOptions } from "@circleco/compass/components/Menu";
import {
  useLocation,
  useNavigate,
  type RegisteredRouter,
} from "@tanstack/react-router";
import type { RouteToPath } from "@tanstack/router-core";
import { useCallback } from "react";

import { t } from "@/i18n-js/instance";
import { useProfileDrawer } from "@/react/components/Drawers/ProfileDrawer";
import { usePartnerOnboardingModal } from "@/react/components/Modals/PartnerOnboardingModal/usePartnerOnboardingModal";
import { useEventsTracker } from "@/react/hooks/analytics/useEventsTracker";
import { useElectronAPI } from "@/react/hooks/utils/useElectronAPI";

interface UseProfileMenuOptionsParams {
  isPreviewMode?: boolean;
}

interface UseProfileMenuOptionsResult {
  isDisabled: boolean;
  menuOptions: MenuOptions;
}

/**
 * Builds the shared profile menu (view profile, edit profile, billing,
 * appearance, sign out, …) so the header avatar and the shell dock footer
 * avatar open the exact same dropdown.
 */
export const useProfileMenuOptions = ({
  isPreviewMode = false,
}: UseProfileMenuOptionsParams = {}): UseProfileMenuOptionsResult => {
  const {
    currentCommunityMember,
    currentCommunitySettings,
    isViewOnlyMasquerading,
  } = usePunditUserContext();
  const { isElectron, electronAPI } = useElectronAPI();
  const location = useLocation();
  const navigate = useNavigate();
  const { show: showMemberProfileDrawer } = useProfileDrawer();
  const shortcutsModal = useKeyboardShortcutsModal();
  const { startPartnerOnboarding } = usePartnerOnboardingModal();
  const { trackEvent } = useEventsTracker();
  const { currentAppearance, toggleAppearance } = useThemeContext();

  const {
    can_access_billing: shouldShowMemberBilling,
    can_access_authentication_page: canAccessAuthenticationPage,
    can_show_paywalls_affiliate: canShowPaywallsAffiliate,
  } = currentCommunityMember?.policies || {};

  const navigateWithState = useCallback(
    <const TTo extends RouteToPath<RegisteredRouter>>(path: TTo) => {
      void navigate({
        to: path,
        state: {
          from: { pathname: location.pathname, search: location.searchStr },
        },
      });
    },
    [navigate, location],
  );

  const handleSignOut = useCallback(() => {
    try {
      if (isElectron) {
        electronAPI?.logout?.();
      }
    } finally {
      window.location.href = signOutPath();
    }
  }, [isElectron, electronAPI]);

  const getDefaultOptions = (): MenuOptions => [
    {
      label: t("view_profile"),
      icon: "people",
      onClick: () => {
        void showMemberProfileDrawer({
          memberPublicId: currentCommunityMember?.public_uid ?? "",
        });
      },
    },
    {
      label: t("edit_profile"),
      icon: "people-edit",
      onClick: () => {
        navigateWithState("/account");
      },
    },
    {
      label: t("notification"),
      icon: "bell",
      onClick: () => {
        navigateWithState("/account/notifications");
      },
    },
  ];

  const getAuthenticationOptions = (): MenuOptions => {
    if (!canAccessAuthenticationPage) return [];
    return [
      {
        label: t("authentication"),
        icon: "lock",
        onClick: () => {
          navigateWithState("/account/authentication");
        },
      },
    ];
  };

  const getBillingOptions = (): MenuOptions => {
    if (!shouldShowMemberBilling) return [];
    return [
      {
        label: t("billing"),
        icon: "credit-card",
        onClick: () => {
          navigateWithState("/account/billing");
        },
      },
    ];
  };

  const getAffiliateOptions = (): MenuOptions => {
    if (!canShowPaywallsAffiliate) return [];
    return [
      {
        label: t("member_profile_modal.affiliate_dashboard.title"),
        icon: "dollar",
        onClick: () => {
          navigateWithState("/account/affiliate");
        },
      },
    ];
  };

  const getPartnerProgramOptions = (): MenuOptions => {
    if (!currentCommunitySettings?.growth_partner_program_enabled) return [];
    return [
      {
        label: t("member_profile_modal.partner_program.title"),
        icon: "heart-handshake",
        onClick: () => {
          trackEvent("circle_partner_dropdown.clicked", {});
          void startPartnerOnboarding();
        },
      },
    ];
  };

  const getPreferencesOptions = (): MenuOptions => [
    {
      label:
        currentAppearance === APPEARANCE.LIGHT
          ? t("sidebar_switch_to_dark_mode")
          : t("sidebar_switch_to_light_mode"),
      icon: currentAppearance === APPEARANCE.LIGHT ? "moon" : "sun",
      onClick: toggleAppearance,
    },
    {
      label: t("header_v3.keyboard_shortcuts"),
      icon: "keyboard",
      onClick: () => {
        void shortcutsModal.show();
      },
    },
  ];

  const getSignOutOptions = (): MenuOptions => [
    "divider",
    {
      label: t("sign_out"),
      icon: "arrow-box-right",
      danger: true,
      onClick: handleSignOut,
    },
  ];

  const menuOptions: MenuOptions = [
    ...getDefaultOptions(),
    ...getAuthenticationOptions(),
    ...getBillingOptions(),
    ...getAffiliateOptions(),
    ...getPartnerProgramOptions(),
    ...getPreferencesOptions(),
    ...getSignOutOptions(),
  ];

  return {
    menuOptions,
    isDisabled: isPreviewMode || isViewOnlyMasquerading,
  };
};
